import {
  Box,
  List,
  ListItem,
  Divider,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Chip,
} from '@mui/material';
// Components
import { OnHoverToolTip, TokenAmount } from '@/common/index';
// Utils
import { toDollar } from '@/utils/index';

const gasHoverText =
  'Transactions require a small gas fee in order to be executed on the blockchain. Gas fees are estimated based on network activity and updated automatically every 30s.';

const OrderSummaryList = ({
  couponBalance,
  couponTitleAndQty,
  currency,
  gasFee,
  gasFeeLoading,
  gasFeePrecision,
  gasFeeToken,
  handleConvertUsdToCoin,
  isCC,
  displayPrecision,
  isExactGas,
  items,
  showGasFeeRow,
  showCouponRow,
  totals,
}) => {
  const { subtotalUsd, totalUsd, totalCoin, subtotalCoin } = totals;

  return (
    <List>
      {items.map((item) => (
        <div key={item.productId}>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar alt={item.productName} src={item.productImage} />
            </ListItemAvatar>
            <ListItemText
              primary={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    sx={{ display: 'inline', paddingRight: '14px' }}
                    component="span"
                    variant="h5"
                    color={item.noLongerAvailable && 'error'}
                  >
                    {item.productName}
                  </Typography>
                  <Chip
                    size="small"
                    label={`Qty ${item?.quantity}`}
                    sx={{
                      color: item?.noLongerAvailable ? 'error' : '',
                    }}
                  />
                </div>
              }
              secondaryTypographyProps={{ component: 'div' }}
              secondary={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '10px',
                  }}
                >
                  <Typography
                    variant="body1"
                    color={item.noLongerAvailable && 'error'}
                  >{`${toDollar(item.subtotal)}`}</Typography>
                  {!isCC ? (
                    <>
                      <Box sx={{ margin: '0 14px' }}>
                        <Divider orientation="vertical" flexItem />
                      </Box>
                      <Typography
                        variant="body1"
                        color={item.noLongerAvailable && 'error'}
                      >
                        <TokenAmount
                          amount={handleConvertUsdToCoin(item.subtotal)}
                          token={currency}
                          precision={displayPrecision}
                        />
                      </Typography>
                    </>
                  ) : null}
                </Box>
              }
            />
          </ListItem>
          <Divider variant="middle" component="li" />
        </div>
      ))}
      {/* ---------------------- Subtotal ---------------------- */}
      <ListItem alignItems="flex-start">
        <ListItemText
          primary={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography component="span" variant="h6">
                Subtotal
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="h6">{`${toDollar(subtotalUsd)}`}</Typography>
                {!isCC && (
                  <Typography
                    variant="h6"
                    sx={{ marginLeft: '16px', textAlign: 'right' }}
                  >
                    <TokenAmount
                      amount={subtotalCoin}
                      token={currency}
                      precision={displayPrecision}
                    />
                  </Typography>
                )}
              </Box>
            </Box>
          }
        />
      </ListItem>
      {/* ---------------------- Gas Fee ---------------------- */}
      {showGasFeeRow && (
        <ListItem>
          <ListItemText
            primary={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box display={'flex'} alignItems={'center'}>
                  <Typography variant="h6" sx={{ marginRight: '10px' }}>
                    {isExactGas ? 'Gas Fee' : 'Est Gas Fee'}
                  </Typography>
                  <OnHoverToolTip hoverText={gasHoverText} width={300} />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  {gasFeeLoading ? (
                    <Typography sx={{ display: 'inline' }} variant="h6">
                      Loading...
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="h6">{`${toDollar(gasFee?.usd)}`}</Typography>
                      <Typography
                        variant="h6"
                        sx={{ marginLeft: '16px', textAlign: 'right' }}
                      >
                        <TokenAmount
                          amount={gasFee?.coin}
                          token={gasFeeToken}
                          precision={gasFeePrecision}
                        />
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            }
          />
        </ListItem>
      )}
      {/* ---------------------- Coupon ---------------------- */}
      {showCouponRow && (
        <ListItem>
          <ListItemText
            primary={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography component="span" variant="h6">
                  {couponTitleAndQty}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="h6">{`-${toDollar(couponBalance)}`}</Typography>
                  {!isCC && (
                    <Typography
                      variant="h6"
                      sx={{ marginLeft: '16px', textAlign: 'right' }}
                    >
                      <TokenAmount
                        amount={handleConvertUsdToCoin(couponBalance)}
                        token={currency}
                        precision={displayPrecision}
                        isNegative
                      />
                    </Typography>
                  )}
                </Box>
              </Box>
            }
          />
        </ListItem>
      )}
      {!isCC && (
        <Divider
          variant="middle"
          component="li"
          sx={showCouponRow && { marginTop: '16px' }}
        />
      )}
      {/* ---------------------- Total ---------------------- */}
      <ListItem>
        <ListItemText
          primary={
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingTop: '14px',
              }}
            >
              <Typography sx={{ display: 'inline' }} variant="h4">
                Total
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography sx={{ display: 'inline' }} variant="h5">
                  {toDollar(totalUsd)}
                </Typography>
                {!isCC && (
                  <Typography
                    variant="h5"
                    sx={{ marginLeft: '16px', textAlign: 'right' }}
                  >
                    <TokenAmount
                      amount={totalCoin}
                      token={currency}
                      precision={displayPrecision}
                    />
                  </Typography>
                )}
              </Box>
            </Box>
          }
        />
      </ListItem>
    </List>
  );
};

export default OrderSummaryList;
